import React from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Users, BookOpen, Shield, CheckCircle, ArrowRight, Star } from 'lucide-react';

function LandingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-2">
              <GraduationCap className="w-8 h-8 text-primary" />
              <span className="text-2xl font-bold text-primary">INTUIT LMS</span>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#features" className="text-gray-600 hover:text-primary transition-colors">Features</a>
              <a href="#pricing" className="text-gray-600 hover:text-primary transition-colors">Pricing</a>
              <Link to="/pricing" className="text-gray-600 hover:text-primary transition-colors">Plans</Link>
            </nav>
            <div className="flex space-x-4">
              <Link to="/signup" className="btn-primary">
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 animate-fade-in">
            Transform Education with
            <span className="text-primary"> INTUIT LMS</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            A comprehensive SaaS Learning Management System designed for colleges and institutions.
            Streamline course management, student tracking, and administrative tasks with our intuitive platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup" className="btn-primary text-lg px-8 py-4">
              Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2 inline" />
            </Link>
            <Link to="/pricing" className="btn-secondary text-lg px-8 py-4">
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose INTUIT LMS?</h2>
            <p className="text-xl text-gray-600">Everything you need to manage your educational institution efficiently</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card text-center animate-slide-up">
              <Users className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Student Management</h3>
              <p className="text-gray-600">Comprehensive student profiles, enrollment tracking, and progress monitoring</p>
            </div>

            <div className="card text-center animate-slide-up" style={{animationDelay: '0.1s'}}>
              <BookOpen className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Course Administration</h3>
              <p className="text-gray-600">Easy course creation, unit management, and curriculum organization</p>
            </div>

            <div className="card text-center animate-slide-up" style={{animationDelay: '0.2s'}}>
              <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Secure & Reliable</h3>
              <p className="text-gray-600">Enterprise-grade security with M-Pesa integration for seamless payments</p>
            </div>

            <div className="card text-center animate-slide-up" style={{animationDelay: '0.3s'}}>
              <CheckCircle className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Automated Workflows</h3>
              <p className="text-gray-600">Streamlined processes for attendance, grading, and administrative tasks</p>
            </div>

            <div className="card text-center animate-slide-up" style={{animationDelay: '0.4s'}}>
              <Star className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Mobile Access</h3>
              <p className="text-gray-600">Full mobile app for students and lecturers with offline capabilities</p>
            </div>

            <div className="card text-center animate-slide-up" style={{animationDelay: '0.5s'}}>
              <GraduationCap className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Analytics & Reports</h3>
              <p className="text-gray-600">Detailed insights and reporting for data-driven decision making</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Institution?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join hundreds of educational institutions already using INTUIT LMS to streamline their operations.
          </p>
          <Link to="/signup" className="bg-white text-primary px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center">
            Get Started Today
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <GraduationCap className="w-8 h-8 text-primary" />
              <span className="text-2xl font-bold">INTUIT LMS</span>
            </div>
            <div className="text-center md:text-right">
              <p className="text-gray-400">© 2025 INTUIT LMS. All rights reserved.</p>
              <p className="text-sm text-gray-500 mt-1">Empowering education through technology</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
